import { ParsedBillData } from '../../../models';
import {
  DuplicateAction,
  DuplicateResolution,
  DuplicateSide,
  DuplicateWarning,
  ExistingFileType,
} from '../../../models/bill-import.models';
import { formatBillPeriod } from '../../../utils/date-format.util';
import { isDistributionDisabled, isSupplyDisabled } from '../bill-ai-flow-bill-review-step/bill-ai-flow-bill-review-step.validation';

/** Exact-date overlaps the user can skip or replace (the others block or pair). */
export function isResolvableDuplicate(dup: DuplicateWarning, pairRejected: boolean): boolean {
  return (!dup.pairable || pairRejected)
    && (dup.overlapType === 'EXACT' || dup.overlapType === 'NEAR_EXACT');
}

/**
 * The action that applies to a duplicate: Skip unless the user chose Replace on a
 * replaceable duplicate for the same existing bill. A missing or stale choice is Skip.
 */
export function effectiveDuplicateAction(
  dup: DuplicateWarning,
  resolution: DuplicateResolution | undefined,
): DuplicateAction {
  return resolution?.action === 'REPLACE'
    && dup.replaceable === true
    && resolution.existingBillId === dup.existingBillId
    ? 'REPLACE' : 'SKIP';
}

/**
 * Restored sessions may hold the retired ENRICH action, or a Replace the server no
 * longer allows; both load as Skip.
 */
export function normalizeRestoredResolutions(
  saved: Record<string, DuplicateResolution> | undefined,
  duplicatesMap: Record<string, DuplicateWarning> | undefined,
): Record<string, DuplicateResolution> {
  const out: Record<string, DuplicateResolution> = {};
  for (const [groupId, resolution] of Object.entries(saved ?? {})) {
    const dup = duplicatesMap?.[groupId];
    const replace = resolution.action === 'REPLACE' && !!dup && effectiveDuplicateAction(dup, resolution) === 'REPLACE';
    out[groupId] = { ...resolution, action: replace ? 'REPLACE' : 'SKIP' };
  }
  return out;
}

const isNonZero = (n: number | null | undefined): boolean => (n ?? 0) !== 0;

/**
 * Which sides an upload carries, as the server derives them: a side is present when it
 * is switched on and any of its charges, tax or total is non-zero.
 */
export function uploadBillSides(data: ParsedBillData | null | undefined): DuplicateSide[] {
  if (!data) return [];
  const sides: DuplicateSide[] = [];
  if (!isSupplyDisabled(data)
    && (isNonZero(data.supplyCharges) || isNonZero(data.supplyTaxes) || isNonZero(data.supplyTotalCost))) {
    sides.push('SUPPLY');
  }
  if (!isDistributionDisabled(data)
    && (isNonZero(data.distributionCharges) || isNonZero(data.distributionTaxes) || isNonZero(data.distributionTotalCost))) {
    sides.push('DISTRIBUTION');
  }
  return sides;
}

/** Chip text for a bill's sides. */
export function sidesLabel(sides: readonly DuplicateSide[] | null | undefined): string {
  const supply = !!sides?.includes('SUPPLY');
  const distribution = !!sides?.includes('DISTRIBUTION');
  if (supply && distribution) return 'Supply + distribution';
  if (supply) return 'Supply only';
  if (distribution) return 'Distribution only';
  return 'Usage only';
}

/** Lower-case noun phrase for a set of sides, e.g. "supply and distribution". */
function sidesPhrase(sides: readonly DuplicateSide[] | null | undefined): string {
  const supply = !!sides?.includes('SUPPLY');
  const distribution = !!sides?.includes('DISTRIBUTION');
  if (supply && distribution) return 'supply and distribution';
  if (supply) return 'supply';
  if (distribution) return 'distribution';
  return '';
}

/** Start and end each within a day of the upload — handled exactly like an exact match, copy aside. */
export function isNearExactDuplicate(dup: DuplicateWarning): boolean {
  return dup.overlapType === 'NEAR_EXACT';
}

export function isRemittanceDuplicate(dup: DuplicateWarning): boolean {
  return dup.existingBillSource === 'REMITTANCE';
}

export function existingFileTypes(dup: DuplicateWarning): ExistingFileType[] {
  return isRemittanceDuplicate(dup) ? [] : dup.existingFileTypes ?? [];
}

/** Label of one "view PDF" link: plain when it is the only file, named when there are two. */
export function existingPdfLinkLabel(type: ExistingFileType, fileCount: number, long: boolean): string {
  if (fileCount > 1) return type === 'UTILITY' ? 'Utility PDF' : 'Supplier PDF';
  return long ? 'View existing PDF' : 'View PDF';
}

/** "Can't replace" line shown in the resolve modal; null when the duplicate is replaceable. */
export function replaceBlockedMessage(dup: DuplicateWarning): string | null {
  if (dup.replaceable) return null;
  const id = dup.existingBillId;
  switch (dup.replaceBlockedReason) {
    case 'WOULD_LOSE_SUPPLY':
      return `Can't replace: bill #${id} also has supply charges and this upload doesn't, so replacing it would lose them.`;
    case 'WOULD_LOSE_DISTRIBUTION':
      return `Can't replace: bill #${id} also has distribution charges and this upload doesn't, so replacing it would lose them.`;
    case 'PARTIAL_OVERLAP':
      return `Can't replace: this bill's dates also overlap another bill on this meter.`;
    default:
      return `Can't replace bill #${id}.`;
  }
}

/** The warning on a bill whose dates match an existing bill, reflecting the current choice. */
export interface DuplicateBannerView {
  tone: 'skip' | 'replace';
  icon: string;
  title: string;
  message: string;
  /** Label of the flip button; null when the bill can't be replaced. */
  actionLabel: string | null;
  /** The action the flip button switches to. */
  actionTarget: DuplicateAction | null;
  files: ExistingFileType[];
}

export function buildDuplicateBanner(
  dup: DuplicateWarning,
  action: DuplicateAction,
  uploadSides: readonly DuplicateSide[],
): DuplicateBannerView {
  const id = dup.existingBillId;
  const dates = formatBillPeriod(dup.existingStartDate, dup.existingEndDate);
  const files = existingFileTypes(dup);

  if (action === 'REPLACE') {
    const linkMoves = isRemittanceDuplicate(dup) ? ' Its supplier payment link moves to this bill.' : '';
    return {
      tone: 'replace',
      icon: 'swap_horiz',
      title: `Replaces bill #${id}`,
      message: `The existing ${dates} bill${files.length > 0 ? ' and its PDF' : ''} will be deleted and this bill imported in its place.${linkMoves}`,
      actionLabel: 'Skip instead',
      actionTarget: 'SKIP',
      files: [],
    };
  }

  const subject = isRemittanceDuplicate(dup) ? `Bill #${id} (from a supplier remittance)` : `Bill #${id} on this meter`;
  const covers = isNearExactDuplicate(dup)
    ? `${subject} already covers ${dates}, a day off from this bill.`
    : `${subject} already covers ${dates}.`;
  if (dup.replaceable) {
    return {
      tone: 'skip',
      icon: 'content_copy',
      title: 'Already imported, will be skipped',
      message: `${covers} This bill won't be imported.`,
      actionLabel: 'Replace instead',
      actionTarget: 'REPLACE',
      files,
    };
  }

  const lead = isRemittanceDuplicate(dup) ? `Bill #${id} (from a supplier remittance)` : `Bill #${id}`;
  if (dup.replaceBlockedReason !== 'WOULD_LOSE_SUPPLY' && dup.replaceBlockedReason !== 'WOULD_LOSE_DISTRIBUTION') {
    return {
      tone: 'skip',
      icon: 'content_copy',
      title: 'Already imported, will be skipped',
      message: dup.replaceBlockedReason === 'PARTIAL_OVERLAP'
        ? `${lead} already covers ${dates}. This bill's dates also overlap another bill on this meter, so it can't replace it.`
        : `${lead} already covers ${dates}. This bill can't replace it.`,
      actionLabel: null,
      actionTarget: null,
      files,
    };
  }
  const existing = sidesPhrase(dup.existingSides);
  const upload = sidesPhrase(uploadSides);
  const existingText = existing ? ` with ${existing} charges` : '';
  const uploadText = upload ? `only has ${upload}` : 'has no supply or distribution charges';
  return {
    tone: 'skip',
    icon: 'content_copy',
    title: 'Already imported, will be skipped',
    message: `${lead} already covers ${isNearExactDuplicate(dup) ? 'nearly the same' : 'these'} dates${existingText}. This bill ${uploadText}, so it can't replace it.`,
    actionLabel: null,
    actionTarget: null,
    files,
  };
}

/** "Mon D" for an ISO date, or '' when it can't be read. */
export function formatMonthDay(isoDate: string | null | undefined): string {
  if (!isoDate) return '';
  const d = new Date(isoDate.length <= 10 ? `${isoDate}T00:00:00` : isoDate);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** "1 replaced an existing bill" / "3 replaced existing bills"; empty for none. */
export function replacedBillsText(count: number | null | undefined): string {
  if (!count) return '';
  return count === 1 ? '1 replaced an existing bill' : `${count} replaced existing bills`;
}

/** Danger line under a chosen Replace. */
export function replaceWarningText(dup: DuplicateWarning): string {
  const id = dup.existingBillId;
  if (isRemittanceDuplicate(dup)) {
    return `Bill #${id} is deleted when you import and its supplier payment link moves to this bill. This can't be undone.`;
  }
  return existingFileTypes(dup).length > 0
    ? `Bill #${id} and its PDF are deleted when you import. This can't be undone.`
    : `Bill #${id} is deleted when you import. This can't be undone.`;
}
